import { Zap } from "lucide-react";
import { fmtUsd, fmtNum } from "@/lib/api";
import { cn } from "@/lib/utils";

export const FuturesPositions = ({ positions }) => {
  return (
    <div data-testid="futures-positions" className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)]">
      <div className="flex items-center gap-2 px-5 h-14 border-b border-[var(--border)]">
        <Zap className="h-4 w-4 text-[var(--warn)]" />
        <h3 className="font-head font-bold text-base">Kaldıraçlı Pozisyonlar (Futures)</h3>
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--surface-hover)]">
              <th className="text-left font-normal py-2.5 px-5">Varlık</th>
              <th className="text-left font-normal py-2.5 px-3">Yön</th>
              <th className="text-right font-normal py-2.5 px-3">Kaldıraç</th>
              <th className="text-right font-normal py-2.5 px-3">Giriş</th>
              <th className="text-right font-normal py-2.5 px-3">Güncel</th>
              <th className="text-right font-normal py-2.5 px-3">Teminat</th>
              <th className="text-right font-normal py-2.5 px-3">Likidite</th>
              <th className="text-right font-normal py-2.5 px-5">K/Z (ROI)</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(!positions || positions.length === 0) && (
              <tr><td colSpan={8} className="text-center py-10 text-[var(--text-faint)] text-sm font-sans">Açık kaldıraçlı pozisyon yok</td></tr>
            )}
            {(positions || []).map((p) => {
              const long = p.side === "LONG";
              const known = p.unrealized_pnl != null;
              const pos = known && p.unrealized_pnl >= 0;
              return (
                <tr key={p.symbol} data-testid={`futures-row-${p.symbol}`} className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)] transition-colors">
                  <td className="py-3 px-5 font-head font-bold">{p.symbol}<span data-testid={`futures-source-${p.symbol}`} className="block text-[10px] font-normal text-[var(--text-faint)]">{p.valuation_source}</span></td>
                  <td className="py-3 px-3">
                    <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded",
                      long ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[#FF3366]/10 text-[var(--neg)]")}>
                      {long ? "LONG" : "SHORT"}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right text-[var(--warn)]">x{p.leverage}</td>
                  <td className="py-3 px-3 text-right text-[var(--text-dim)]">{fmtUsd(p.entry_price)}</td>
                  <td data-testid={`futures-current-price-${p.symbol}`} className="py-3 px-3 text-right">{fmtUsd(p.current_price)}</td>
                  <td className="py-3 px-3 text-right text-[var(--text-dim)]">{fmtUsd(p.margin)}</td>
                  <td className="py-3 px-3 text-right text-[var(--neg)]">{fmtUsd(p.liq_price)}</td>
                  <td data-testid={`futures-pnl-${p.symbol}`} className={cn("py-3 px-5 text-right font-bold", !known ? "text-[var(--warn)]" : pos ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                    {known ? <>{pos ? "+" : ""}{fmtUsd(p.unrealized_pnl)}<div className="text-[10px] font-normal">{pos ? "+" : ""}{p.roi_pct}%</div></> : "Doğrulanamadı"}
                    {p.valuation_warning && <p data-testid={`futures-valuation-warning-${p.symbol}`} role="alert" className="text-[10px] font-normal max-w-48 ml-auto">{p.valuation_warning}</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-[var(--border)]">
        {(!positions || positions.length === 0) && (
          <p className="text-center py-8 text-[var(--text-faint)] text-sm">Açık kaldıraçlı pozisyon yok</p>
        )}
        {(positions || []).map((p) => {
          const long = p.side === "LONG";
          const known = p.unrealized_pnl != null;
          const pos = known && p.unrealized_pnl >= 0;
          return (
            <div key={p.symbol} data-testid={`futures-card-${p.symbol}`} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-head font-bold text-base">{p.symbol}</span>
                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                    long ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[#FF3366]/10 text-[var(--neg)]")}>
                    {long ? "LONG" : "SHORT"}
                  </span>
                  <span className="text-[10px] font-mono text-[var(--warn)]">x{p.leverage}</span>
                </div>
                <div data-testid={`futures-mobile-pnl-${p.symbol}`} className={cn("text-right font-mono font-bold", !known ? "text-[var(--warn)]" : pos ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                  {known ? <>{pos ? "+" : ""}{fmtUsd(p.unrealized_pnl)}<div className="text-[10px] font-normal">{pos ? "+" : ""}{p.roi_pct}%</div></> : <span className="text-xs">Doğrulanamadı</span>}
                </div>
              </div>
              <p data-testid={`futures-mobile-source-${p.symbol}`} className="text-[10px] text-[var(--text-faint)] mb-2">{p.valuation_source}</p>
              {p.valuation_warning && <p data-testid={`futures-mobile-valuation-warning-${p.symbol}`} role="alert" className="text-[10px] text-[var(--warn)] mb-2">{p.valuation_warning}</p>}
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Giriş</span><span className="text-[var(--text-dim)]">{fmtUsd(p.entry_price)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Güncel</span><span>{fmtUsd(p.current_price)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Teminat</span><span className="text-[var(--text-dim)]">{fmtUsd(p.margin)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Likidite</span><span className="text-[var(--neg)]">{fmtUsd(p.liq_price)}</span></div>
              </div>
            </div>
          );
        })}
      </div>
      {positions?.length > 0 && <p data-testid="futures-valuation-scope" className="px-5 py-3 text-[10px] text-[var(--text-faint)]">Fonlama ve gelecekteki kapanış ücreti hariç. Yeni botlarda değerleme Gate USDT-M verisidir; veri veya giriş ücreti eksikse K/Z hesaplanmaz.</p>}
    </div>
  );
};
